class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.renderBundlePrompt();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      this.renderBundlePrompt();
    });
  }

  renderBundlePrompt() {
    if (this.classList.contains('is-empty')) return;

    const drawer = this.querySelector('.drawer__inner');
    const header = this.querySelector('.drawer__header');
    if (!drawer || !header) return;

    if (!document.getElementById('sysa-cart-bundle-style')) {
      const style = document.createElement('style');
      style.id = 'sysa-cart-bundle-style';
      style.textContent = `
        .sysa-cart-bundle {
          margin: 0 0 1.4rem;
          padding: 1.4rem;
          border-radius: 1.8rem;
          background: #0b0b0d;
          color: #fff;
          box-shadow: 0 16px 38px rgba(0,0,0,.16);
        }
        .sysa-cart-bundle__eyebrow {
          margin: 0 0 .45rem;
          color: rgba(255,255,255,.62);
          font-size: 1.05rem;
          font-weight: 800;
          letter-spacing: .14em;
          text-transform: uppercase;
        }
        .sysa-cart-bundle__title {
          margin: 0;
          color: #fff;
          font-size: 1.65rem;
          line-height: 1.12;
          font-weight: 900;
          letter-spacing: -.03em;
        }
        .sysa-cart-bundle__sub {
          margin: .65rem 0 1rem;
          color: rgba(255,255,255,.72);
          font-size: 1.25rem;
          line-height: 1.35;
        }
        .sysa-cart-bundle__steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: .55rem;
          margin: 0 0 1rem;
        }
        .sysa-cart-bundle__step {
          border: 1px solid rgba(255,255,255,.16);
          background: rgba(255,255,255,.06);
          border-radius: 1.2rem;
          padding: .8rem .7rem;
          text-align: center;
        }
        .sysa-cart-bundle__step.is-active {
          background: #fff;
          color: #0b0b0d;
          border-color: #fff;
        }
        .sysa-cart-bundle__step strong {
          display: block;
          font-size: 1.25rem;
          line-height: 1;
        }
        .sysa-cart-bundle__step span {
          display: block;
          margin-top: .35rem;
          font-size: .95rem;
          opacity: .72;
          text-transform: uppercase;
          letter-spacing: .06em;
          font-weight: 800;
        }
        .sysa-cart-bundle__button {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 4.4rem;
          border-radius: 999px;
          background: #fff;
          color: #0b0b0d;
          text-decoration: none;
          font-size: 1.25rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .04em;
        }
      `;
      document.head.appendChild(style);
    }

    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const itemCount = cart.item_count || 0;
        if (!itemCount) return;

        let title = 'Bundle more. Save more.';
        let sub = 'Add one more poster to unlock 10% off your order.';
        let active = 1;

        if (itemCount >= 3) {
          title = '15% off + free shipping unlocked.';
          sub = 'You have 3+ posters in cart. Head to checkout when you’re ready.';
          active = 3;
        } else if (itemCount >= 2) {
          title = '10% off unlocked.';
          sub = 'You have 2 posters in cart. Add one more to unlock 15% off + free shipping.';
          active = 2;
        }

        const existing = this.querySelector('.sysa-cart-bundle');
        if (existing) existing.remove();

        const box = document.createElement('div');
        box.className = 'sysa-cart-bundle';
        box.innerHTML = `
          <p class="sysa-cart-bundle__eyebrow">Bundle offer</p>
          <h3 class="sysa-cart-bundle__title">${title}</h3>
          <p class="sysa-cart-bundle__sub">${sub}</p>
          <div class="sysa-cart-bundle__steps" aria-label="Bundle discount tiers">
            <div class="sysa-cart-bundle__step ${active >= 2 ? 'is-active' : ''}"><strong>2</strong><span>10% off</span></div>
            <div class="sysa-cart-bundle__step ${active >= 3 ? 'is-active' : ''}"><strong>3+</strong><span>15% off + free ship</span></div>
          </div>
          <a class="sysa-cart-bundle__button" href="/collections/best-sellers">Add another poster</a>
        `;

        header.insertAdjacentElement('afterend', box);
      })
      .catch(() => {});
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
